import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import { 
  Users, 
  Trophy, 
  Target, 
  TrendingUp, 
  CheckCircle,
  Clock,
  DollarSign
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

export const Dashboard: React.FC = () => {
  const { isMobile } = useResponsive();
  
  const { data: orgSummary, isLoading: orgLoading, error: orgError } = useQuery({
    queryKey: ['organizationSummary'],
    queryFn: dashboardApi.getOrganizationSummary
  });

  const { data: memberTokens, isLoading: membersLoading, error: membersError } = useQuery({
    queryKey: ['memberTokens'],
    queryFn: dashboardApi.getMemberTokens
  });

  if (orgLoading || membersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-san-green"></div>
      </div>
    );
  }

  if (orgError || membersError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-2">Error loading dashboard data:</p>
          <p className="text-gray-600">{(orgError as Error)?.message || (membersError as Error)?.message}</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const topMembers = memberTokens?.slice(0, 10) || [];
  const chartData = topMembers.map(member => ({
    name: member.member_name.length > 15 
      ? member.member_name.substring(0, 15) + '...' 
      : member.member_name,
    tokens: member.tokens_earned,
    target: member.token_target,
    completion: member.completion_percentage
  }));

  const statusData = [
    { name: 'Completed', value: orgSummary?.members_completed || 0, color: '#4ade80' },
    { name: 'Pending', value: orgSummary?.members_pending || 0, color: '#f59e0b' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-san-gray-900 sm:text-3xl sm:truncate">
            Dashboard
          </h1>
          <p className="mt-1 text-sm text-san-gray-500">
            Overview of SAN token system for {orgSummary?.current_year}
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-6 w-6 sm:h-8 sm:w-8 text-san-blue" />
            </div>
            <div className="ml-4 sm:ml-5 w-0 flex-1">
              <dl>
                <dt className="text-xs sm:text-sm font-medium text-san-gray-500 truncate">Total Members</dt>
                <dd className="text-lg sm:text-2xl font-bold text-san-gray-900">{orgSummary?.total_members.toLocaleString()}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-san-green" />
            </div>
            <div className="ml-4 sm:ml-5 w-0 flex-1">
              <dl>
                <dt className="text-xs sm:text-sm font-medium text-san-gray-500 truncate">Total Tokens Earned</dt>
                <dd className="text-lg sm:text-2xl font-bold text-san-gray-900">{orgSummary?.total_tokens_earned.toLocaleString()}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Target className="h-6 w-6 sm:h-8 sm:w-8 text-san-blue" />
            </div>
            <div className="ml-4 sm:ml-5 w-0 flex-1">
              <dl>
                <dt className="text-xs sm:text-sm font-medium text-san-gray-500 truncate">Target Tokens</dt>
                <dd className="text-lg sm:text-2xl font-bold text-san-gray-900">{orgSummary?.total_target_tokens.toLocaleString()}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-san-green" />
            </div>
            <div className="ml-4 sm:ml-5 w-0 flex-1">
              <dl>
                <dt className="text-xs sm:text-sm font-medium text-san-gray-500 truncate">Avg Completion</dt>
                <dd className="text-lg sm:text-2xl font-bold text-san-gray-900">{orgSummary?.average_completion.toFixed(1)}%</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="bg-green-50 overflow-hidden shadow rounded-lg border border-green-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-green-800 truncate">Completed Members</p>
              <p className="text-lg sm:text-2xl font-bold text-green-900">{orgSummary?.members_completed}</p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 overflow-hidden shadow rounded-lg border border-yellow-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-yellow-800 truncate">Pending Members</p>
              <p className="text-lg sm:text-2xl font-bold text-yellow-900">{orgSummary?.members_pending}</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 overflow-hidden shadow rounded-lg border border-blue-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-blue-800 truncate">Completion Rate</p>
              <p className="text-lg sm:text-2xl font-bold text-blue-900">
                {((orgSummary?.members_completed || 0) / (orgSummary?.total_members || 1) * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Top Members Chart */}
        <div className="lg:col-span-2 bg-white overflow-hidden shadow rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-medium text-san-gray-900 mb-3 sm:mb-4">
            Top 10 Members by Tokens Earned
          </h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={chartData} 
                margin={{ 
                  top: 20, 
                  right: 10, 
                  left: 10, 
                  bottom: isMobile ? 40 : 60 
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={isMobile ? -90 : -45}
                  textAnchor="end"
                  height={isMobile ? 60 : 80}
                  interval={0}
                  fontSize={isMobile ? 10 : 12}
                />
                <YAxis fontSize={isMobile ? 10 : 12} />
                <Tooltip />
                <Bar dataKey="tokens" fill="#4ade80" name="Tokens Earned" />
                <Bar dataKey="target" fill="#e5e7eb" name="Target" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Completion Status Pie Chart */}
        <div className="bg-white overflow-hidden shadow rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-medium text-san-gray-900 mb-3 sm:mb-4">
            Member Status
          </h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={isMobile ? 40 : 60}
                  outerRadius={isMobile ? 70 : 100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            {statusData.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-sm text-san-gray-600">{item.name}</span>
                </div>
                <span className="text-sm font-medium text-san-gray-900">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-san-gray-900">
            Member Overview
          </h3>
          <span className="text-sm text-san-gray-500">
            Showing top performers
          </span>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-san-gray-200">
            <thead className="bg-san-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-san-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-san-gray-500 uppercase tracking-wider">
                  Tokens Earned
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-san-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-san-gray-500 uppercase tracking-wider">
                  Completion
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-san-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-san-gray-200">
              {memberTokens?.slice(0, 10).map((member) => (
                <tr key={member.member_id} className="hover:bg-san-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-san-gray-900">
                      {member.member_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-san-gray-900">
                      {member.tokens_earned.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-san-gray-500">
                      {member.token_target.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-1 bg-san-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className="bg-san-green h-2 rounded-full" 
                          style={{ width: `${Math.min(member.completion_percentage, 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-san-gray-900">
                        {member.completion_percentage.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      member.completion_percentage >= 100
                        ? 'bg-green-100 text-green-800'
                        : member.completion_percentage >= 50
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {member.completion_percentage >= 100 ? 'Complete' : 'In Progress'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};